import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Megaphone, Save, Plus, Trash2, Copy, Send, Rocket, Home, FileText, Eye, Mail, Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, List, ListOrdered, Heading, UserSquare2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "./ui/dialog";

const ToolbarBtn = ({ onClick, children, title, testid: t }) => (
  <button type="button" onClick={onClick} title={title} data-testid={t}
    className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white hover:bg-slate-100 hover:border-brand text-slate-700 hover:text-brand transition-colors">
    {children}
  </button>
);

/* Iter121k · Toolbar di formattazione compatta per le textarea dei modelli. */
function FormatToolbar({ textareaRef, value, onChange, testid, titolareData }) {
  const applyWrap = (before, after = before) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const sel = value.slice(start, end);
    const next = value.slice(0, start) + before + sel + after + value.slice(end);
    onChange(next);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + before.length, end + before.length); }, 0);
  };
  const applyLinePrefix = (prefix) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const before = value.slice(0, start);
    const lineStart = before.lastIndexOf("\n") + 1;
    const linesEnd = value.indexOf("\n", end);
    const blockEnd = linesEnd === -1 ? value.length : linesEnd;
    const block = value.slice(lineStart, blockEnd);
    const newBlock = block.split("\n").map((ln) => prefix + ln).join("\n");
    const next = value.slice(0, lineStart) + newBlock + value.slice(blockEnd);
    onChange(next);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + prefix.length, end + prefix.length); }, 0);
  };
  const applyAlign = (dir) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const sel = value.slice(start, end) || "testo";
    const wrapper = `<div align="${dir}">${sel}</div>`;
    onChange(value.slice(0, start) + wrapper + value.slice(end));
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start, start + wrapper.length); }, 0);
  };
  // Iter122k44 · Inserisce il blocco "Legale rapp.te" (dati amministratore) dai dati Studio
  const insertLegaleRappte = () => {
    if (!titolareData) return;
    const t = titolareData;
    const rs = (t.ragione_sociale || "").trim();
    const legale = (t.legale_rappresentante || "").trim();
    if (!rs && !legale) {
      toast.error("Compila prima Ragione sociale e Legale rappresentante in Profilo Studio → Titolare del trattamento.");
      return;
    }
    if (!legale) {
      toast.warning("Manca il Legale rappresentante in Profilo Studio → Titolare del trattamento. Inserisco solo la ragione sociale.");
    }
    const indirizzo = (t.indirizzo_completo || t.indirizzo || "").trim();
    const cf = (t.codice_fiscale || "").trim();
    const piva = (t.partita_iva || "").trim();
    const email = (t.email || "").trim();
    const pec = (t.pec || "").trim();
    const tel = (t.telefono || "").trim();
    // Blocco formattato (Markdown-friendly)
    const lines = ["**Legale rapp.te**"];
    if (legale) lines.push(legale);
    if (rs) lines.push(rs);
    if (indirizzo) lines.push(indirizzo);
    if (cf) lines.push(`C.F. ${cf}`);
    if (piva) lines.push(`P.IVA ${piva}`);
    if (tel) lines.push(`Tel. ${tel}`);
    if (email) lines.push(`E-mail: ${email}`);
    if (pec) lines.push(`PEC: ${pec}`);
    const block = lines.join("  \n") + "\n";
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const next = value.slice(0, start) + block + value.slice(end);
    onChange(next);
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + block.length, start + block.length); }, 0);
  };
  return (
    <div className="flex flex-wrap gap-1 mb-1 p-1 bg-slate-50 border border-slate-200 rounded-lg" data-testid={testid}>
      <ToolbarBtn onClick={() => applyWrap("**")} title="Grassetto (Ctrl+B)" testid={`${testid}-bold`}><Bold className="w-4 h-4" /></ToolbarBtn>
      <ToolbarBtn onClick={() => applyWrap("*")} title="Corsivo (Ctrl+I)" testid={`${testid}-italic`}><Italic className="w-4 h-4" /></ToolbarBtn>
      <ToolbarBtn onClick={() => applyWrap("<u>", "</u>")} title="Sottolineato" testid={`${testid}-underline`}><Underline className="w-4 h-4" /></ToolbarBtn>
      <div className="w-px my-1 bg-slate-300" />
      <ToolbarBtn onClick={() => applyLinePrefix("## ")} title="Titolo sezione" testid={`${testid}-h2`}><Heading className="w-4 h-4" /></ToolbarBtn>
      <ToolbarBtn onClick={() => applyLinePrefix("- ")} title="Elenco puntato" testid={`${testid}-ul`}><List className="w-4 h-4" /></ToolbarBtn>
      <ToolbarBtn onClick={() => applyLinePrefix("1. ")} title="Elenco numerato" testid={`${testid}-ol`}><ListOrdered className="w-4 h-4" /></ToolbarBtn>
      <div className="w-px my-1 bg-slate-300" />
      <ToolbarBtn onClick={() => applyAlign("left")} title="Allinea a sinistra" testid={`${testid}-align-left`}><AlignLeft className="w-4 h-4" /></ToolbarBtn>
      <ToolbarBtn onClick={() => applyAlign("center")} title="Centra" testid={`${testid}-align-center`}><AlignCenter className="w-4 h-4" /></ToolbarBtn>
      <ToolbarBtn onClick={() => applyAlign("right")} title="Allinea a destra" testid={`${testid}-align-right`}><AlignRight className="w-4 h-4" /></ToolbarBtn>
      {/* Iter122k44 · Inserisce dati "Legale rapp.te" presi dal Titolare del trattamento */}
      <div className="w-px my-1 bg-slate-300" />
      <button type="button" onClick={insertLegaleRappte}
        title="Inserisci dati Legale rapp.te (Amministratore) dal Profilo Studio"
        data-testid={`${testid}-legale-rappte`}
        className="h-8 px-2 flex items-center gap-1 rounded border border-slate-200 bg-white hover:bg-slate-100 hover:border-brand text-slate-700 hover:text-brand transition-colors text-xs font-semibold">
        <UserSquare2 className="w-4 h-4" /> Legale rapp.te
      </button>
    </div>
  );
}

const DEST = [
  { value: "condomini", label: "Condòmini" },
  { value: "fornitori", label: "Fornitori" },
  { value: "entrambi", label: "Condòmini e Fornitori" },
];
const CATEGORIE = ["Informativa", "Avviso", "Richiesta dati"];
const TIPI_CAMPO = [
  { value: "testo", label: "Testo" }, { value: "numero", label: "Numero" },
  { value: "data", label: "Data" }, { value: "scelta", label: "Scelta" }, { value: "sino", label: "Sì / No" },
];
const EMPTY = { titolo: "", destinatario: "condomini", categoria: "Informativa", oggetto: "", testo: "", campi: [] };

export const ComunicazioniModelli = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(""); // id selezionato ("" = nuovo)
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [launch, setLaunch] = useState(false);
  const [condomini, setCondomini] = useState([]);
  const [lc, setLc] = useState({ invia_condomini: true, condominio_id: "", invia_fornitori: false, categoria_fornitore: "" });
  const [racCond, setRacCond] = useState("");
  // Iter122fy · RAC selettivo per soggetto
  const [racSoggetti, setRacSoggetti] = useState([]);       // lista { id, nome, email, unita_collegate }
  const [racSoggettiSel, setRacSoggettiSel] = useState(new Set()); // set di soggetto_id selezionati
  const [racSoggettiBusy, setRacSoggettiBusy] = useState(false);
  const [racSoggettiSearch, setRacSoggettiSearch] = useState("");
  const [schede, setSchede] = useState({ scheda_condomino_testo: "", scheda_fornitore_testo: "" });
  const [schedeBusy, setSchedeBusy] = useState(false);
  const [anteprima, setAnteprima] = useState(null);
  // Iter121k · refs per le textarea per la toolbar di formattazione
  const schedaTextareaRef = useRef(null);
  const modelloTextareaRef = useRef(null);
  // Iter122k44 · dati Titolare del trattamento per il pulsante "Legale rapp.te"
  const [titolareData, setTitolareData] = useState(null);

  const SCHEDA_META = {
    "__scheda_condomino__": { campo: "scheda_condomino_testo", ruolo: "condomino", label: "Condòmini" },
    "__scheda_fornitore__": { campo: "scheda_fornitore_testo", ruolo: "fornitore", label: "Fornitori" },
  };

  // Iter123k260 · Modelli derivanti dai Moduli bacheca (predefiniti + custom)
  const [moduliBacheca, setModuliBacheca] = useState([]);
  const [targetFieldsAnag, setTargetFieldsAnag] = useState({});
  // Mapping campo_modulo → campo scheda condomino, per auto-apply al submit
  const [autoApplyMapping, setAutoApplyMapping] = useState({});

  const load = (selectId) => api.get("/comunicazioni-modelli")
    .then((r) => {
      setList(r.data);
      if (selectId) { const m = r.data.find((x) => x.id === selectId); if (m) { setSel(m.id); setForm({ ...EMPTY, ...m }); } }
    })
    .catch(() => {});
  useEffect(() => {
    load();
    api.get("/condomini").then((r) => setCondomini(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSchede({ scheda_condomino_testo: r.data.scheda_condomino_testo || "", scheda_fornitore_testo: r.data.scheda_fornitore_testo || "" })).catch(() => {});
    // Iter122k44 · carica dati Titolare del trattamento per il pulsante "Legale rapp.te"
    api.get("/me/tenant/titolare").then((r) => setTitolareData(r.data)).catch(() => {});
    // Iter123k260 · carica anche i moduli bacheca come modelli selezionabili
    api.get("/raccolte/modelli-moduli").then((r) => {
      setModuliBacheca(r.data?.modelli || []);
      setTargetFieldsAnag(r.data?.target_fields || {});
    }).catch(() => {});
  }, []);

  const onSelect = (id) => {
    setSel(id);
    setAutoApplyMapping({});
    if (!id || id.startsWith("__")) { setForm(EMPTY); return; }
    // Iter123k260 · se è stato selezionato un Modulo bacheca → prefill da modulo
    if (id.startsWith("modulo:")) {
      const mid = id.slice("modulo:".length);
      const m = moduliBacheca.find((x) => x.id === mid);
      if (m) {
        setForm({
          titolo: m.titolo || "",
          destinatario: "condomino",
          categoria: "",
          oggetto: m.titolo || "",
          testo: m.descrizione || "",
          campi: m.campi || [],
        });
        setAutoApplyMapping(m.auto_apply_mapping || {});
      }
      return;
    }
    const m = list.find((x) => x.id === id);
    if (m) setForm({ titolo: m.titolo, destinatario: m.destinatario, categoria: m.categoria, oggetto: m.oggetto || "", testo: m.testo || "", campi: m.campi || [] });
  };

  const salvaScheda = async (campo) => {
    setSchedeBusy(true);
    try { await api.put("/settings", { [campo]: schede[campo] }); toast.success("Scheda salvata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setSchedeBusy(false); }
  };
  const apriAnteprimaEmail = async (ruolo) => {
    try { const { data } = await api.get("/manuali/anteprima-email", { params: { ruolo } }); setAnteprima(data); }
    catch { toast.error("Anteprima non disponibile"); }
  };
  const scaricaScheda = async (ruolo) => {
    try {
      const res = await api.get("/manuali/scheda/pdf", { params: { ruolo }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `scheda_informativa_${ruolo}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
  };

  const avviaRac = async () => {
    setBusy(true);
    try {
      const params = racCond ? { condominio_id: racCond } : {};
      const bodyPayload = racCond && racSoggettiSel.size > 0
        ? { soggetti_ids: Array.from(racSoggettiSel) }
        : null;
      const { data } = await api.post("/proprieta/sollecita", bodyPayload, { params });
      // Iter122m · report dettagliato quando il condominio è migrato Unit-First
      const uf = data.unit_first;
      if (uf) {
        const parts = [
          `${data.sollecitati} richiesta/e inviate`,
          uf.senza_email ? `${uf.senza_email} senza email` : null,
          uf.errori ? `${uf.errori} errori` : null,
        ].filter(Boolean).join(" · ");
        if (data.sollecitati === 0) {
          // Segnala il motivo con un toast lungo
          if (uf.totale_soggetti_attivi === 0) {
            toast.warning("Nessun soggetto attivo nel condominio: verifica che i condòmini siano stati importati e collegati a un'unità.", { duration: 10000 });
          } else if (uf.senza_email === uf.totale_soggetti_attivi) {
            toast.warning(`Nessuna richiesta inviata: tutti i ${uf.totale_soggetti_attivi} soggetti sono privi di email. Aggiungi le email in Anagrafica > Soggetti prima di riavviare la campagna.`, { duration: 12000 });
          } else {
            toast.warning(`Campagna RAC: 0 inviate · ${uf.senza_email} senza email · ${uf.errori} errori. Verifica in Anagrafica > Soggetti.`, { duration: 10000 });
          }
        } else {
          toast.success(`Campagna RAC avviata: ${parts}.`);
        }
      } else {
        toast.success(`Campagna RAC avviata: ${data.sollecitati} richiesta/e inviate ai condòmini con dati incompleti.`);
      }
      // Reset selezione dopo l'invio
      setRacSoggettiSel(new Set());
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Avvio non riuscito"); }
    finally { setBusy(false); }
  };

  // Iter122fy · carica i soggetti del condominio selezionato per consentire invio selettivo
  useEffect(() => {
    if (!racCond) {
      setRacSoggetti([]); setRacSoggettiSel(new Set()); setRacSoggettiSearch("");
      return;
    }
    setRacSoggettiBusy(true);
    api.get("/refactor/soggetti", { params: { condominio_id: racCond } })
      .then((r) => { setRacSoggetti(r.data.soggetti || []); setRacSoggettiSel(new Set()); setRacSoggettiSearch(""); })
      .catch(() => setRacSoggetti([]))
      .finally(() => setRacSoggettiBusy(false));
  }, [racCond]);

  const setCampo = (i, patch) => setForm((f) => ({ ...f, campi: f.campi.map((c, idx) => idx === i ? { ...c, ...patch } : c) }));
  const addCampo = () => setForm((f) => ({ ...f, campi: [...(f.campi || []), { etichetta: "", tipo: "testo", opzioni: [], obbligatorio: false }] }));
  const delCampo = (i) => setForm((f) => ({ ...f, campi: f.campi.filter((_, idx) => idx !== i) }));

  const avvia = async () => {
    if (!form.titolo.trim()) { toast.error("Salva prima il modello con un titolo"); return; }
    if (!(form.campi || []).length) { toast.error("Aggiungi almeno un campo da raccogliere"); return; }
    if (!lc.invia_condomini && !lc.invia_fornitori) { toast.error("Seleziona i destinatari"); return; }
    setBusy(true);
    try {
      const payload = {
        modello_id: (sel && !sel.startsWith("modulo:")) ? sel : null,
        titolo: form.titolo, descrizione: form.testo, campi: form.campi,
        invia_condomini: lc.invia_condomini, condominio_id: lc.condominio_id || null,
        invia_fornitori: lc.invia_fornitori, categoria_fornitore: lc.categoria_fornitore || null,
      };
      // Iter123k260 · solo se il destinatario sono i condòmini ha senso l'auto-apply
      // sulla scheda unità, filtrando per campi mappati che hanno un target valido.
      const applicabili = Object.fromEntries(
        Object.entries(autoApplyMapping || {}).filter(([, t]) => t)
      );
      if (lc.invia_condomini && Object.keys(applicabili).length > 0) {
        payload.auto_apply_mapping = applicabili;
      }
      const { data } = await api.post("/raccolte", payload);
      toast.success(`Raccolta avviata: inviata a ${data.destinatari} destinatari.`);
      setLaunch(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Avvio non riuscito"); }
    finally { setBusy(false); }
  };

  const salva = async () => {
    if (!form.titolo.trim()) { toast.error("Inserisci un titolo per il modello"); return; }
    setBusy(true);
    try {
      if (sel) { await api.put(`/comunicazioni-modelli/${sel}`, form); toast.success("Modello aggiornato"); await load(sel); }
      else { const { data } = await api.post("/comunicazioni-modelli", form); toast.success("Modello creato"); await load(data.id); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };

  const elimina = async () => {
    if (!sel) return;
    if (!window.confirm("Eliminare questo modello di comunicazione?")) return;
    setBusy(true);
    try { await api.delete(`/comunicazioni-modelli/${sel}`); toast.success("Modello eliminato"); setSel(""); setForm(EMPTY); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione non riuscita"); }
    finally { setBusy(false); }
  };

  const copia = async () => {
    try { await navigator.clipboard.writeText(`${form.oggetto}\n\n${form.testo}`); toast.success("Testo copiato negli appunti"); }
    catch { toast.error("Copia non riuscita"); }
  };

  const inviaCome = async (canale) => {
    const key = canale === "fornitori" ? "ginius_prefill_avviso_forn" : "ginius_prefill_avviso";
    const payload = { titolo: form.oggetto || form.titolo, testo: form.testo };
    // Iter122cx · se il modello ha un allegato PDF predefinito, materializzalo e lo passa alla Bacheca
    const currentModel = sel ? list.find((x) => x.id === sel) : null;
    if (currentModel?.allegato_predefinito && canale === "condomini") {
      try {
        const { data } = await api.post(`/comunicazioni-modelli/${sel}/allegato-predefinito`);
        payload.allegati = [data];
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Allegato predefinito non disponibile: procedo senza.");
      }
    }
    localStorage.setItem(key, JSON.stringify(payload));
    navigate(canale === "fornitori" ? "/comunicazioni-fornitori" : "/bacheca");
  };

  return (
    <Card className="p-5 border-2 mt-4" data-testid="modelli-comunicazione-card">
      <div className="flex items-center gap-2 mb-1"><Megaphone className="w-5 h-5 text-brand" /><h3 className="font-display text-lg font-bold">Modelli di comunicazione e moduli</h3></div>
      <p className="text-sm text-slate-500 mb-4">Tipologie di comunicazioni standard, riutilizzabili e aggiornabili (campagne informative, avvisi, richieste/raccolta dati). Seleziona un modello per modificarlo o creane uno nuovo, poi usalo per inviare un avviso ai condòmini o una comunicazione ai fornitori.</p>

      <div className="grid lg:grid-cols-[280px_1fr] gap-4">
        <div>
          <label className="block text-sm font-bold text-slate-700 mb-1">Modello</label>
          <select data-testid="modello-select" value={sel} onChange={(e) => onSelect(e.target.value)}
            className="h-12 w-full px-3 border-2 border-slate-300 rounded-lg bg-white text-base">
            <option value="">➕ Nuovo modello…</option>
            <option value="__rac__">📋 Campagna RAC — Anagrafe Condominiale</option>
            <option value="__scheda_condomino__">📄 Scheda informativa & email attivazione — Condòmini</option>
            <option value="__scheda_fornitore__">📄 Scheda informativa & email attivazione — Fornitori</option>
            {list.map((m) => <option key={m.id} value={m.id}>{m.titolo} {m.built_in ? "" : "✎"}</option>)}
            {/* Iter123k260 · Moduli bacheca come modelli di raccolta dati */}
            {moduliBacheca.length > 0 && (
              <optgroup label="📋 Moduli bacheca">
                {moduliBacheca.map((m) => (
                  <option key={m.id} value={`modulo:${m.id}`}>{m.titolo}</option>
                ))}
              </optgroup>
            )}
          </select>
          {sel && sel.startsWith("modulo:") && (
            <div data-testid="modulo-bacheca-info" className="mt-2 p-2 rounded-lg bg-indigo-50 border border-indigo-200 text-xs text-indigo-800">
              Modulo bacheca selezionato: i campi, le descrizioni e il mapping di aggiornamento scheda unità sono stati pre-compilati.
            </div>
          )}
          <Button data-testid="modello-nuovo" variant="outline" className="mt-2 h-10 w-full" onClick={() => onSelect("")}>
            <Plus className="w-4 h-4 mr-1" /> Nuovo modello
          </Button>
          {sel && !sel.startsWith("__") && !sel.startsWith("modulo:") && (
            <Button data-testid="modello-elimina" variant="outline" disabled={busy} onClick={elimina}
              className="mt-2 h-10 w-full border-red-300 text-red-700">
              <Trash2 className="w-4 h-4 mr-1" /> Elimina modello
            </Button>
          )}
        </div>

        <div className="space-y-3 min-w-0">
          {sel === "__rac__" ? (
            <div className="space-y-4" data-testid="rac-campaign-panel">
              <div className="border-2 border-[#1C7C54]/30 rounded-lg p-4 bg-[#1C7C54]/5">
                <div className="flex items-center gap-2 mb-1 text-[#1C7C54] font-bold"><Home className="w-5 h-5" /> Campagna RAC — Registro di Anagrafe Condominiale</div>
                <p className="text-sm text-slate-600">Invia ai condòmini con <b>dati di proprietà incompleti</b> un'email con un <b>modulo compilabile senza login</b>: i dati raccolti confluiscono automaticamente nella sezione <b>Anagrafica proprietà</b>. L'oggetto, l'elenco dei dati mancanti e il link al modulo vengono generati automaticamente per ciascuna unità immobiliare.</p>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Ambito della campagna</label>
                <select data-testid="rac-condominio-select" value={racCond} onChange={(e) => setRacCond(e.target.value)}
                  className="h-12 w-full px-3 border-2 border-slate-300 rounded-lg bg-white text-base">
                  <option value="">Tutti i condomìni</option>
                  {condomini.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
                <p className="text-xs text-slate-400 mt-1">Verranno contattati solo i condòmini con scheda proprietà incompleta e un account/email valido.</p>
              </div>

              {/* Iter122fy · Selettore soggetti (uno o più) del condominio scelto */}
              {racCond && (
                <div data-testid="rac-soggetti-panel">
                  <div className="flex flex-wrap items-baseline gap-2 mb-1">
                    <label className="text-sm font-bold text-slate-700">Soggetti a cui inviare</label>
                    <span className="text-xs text-slate-500">
                      {racSoggettiBusy ? "Caricamento…" : (
                        racSoggettiSel.size === 0
                          ? `Nessuno selezionato → invio a TUTTI i ${racSoggetti.length} soggetti del condominio`
                          : `${racSoggettiSel.size} su ${racSoggetti.length} selezionati`
                      )}
                    </span>
                    {racSoggetti.length > 0 && (
                      <div className="ml-auto flex gap-1 text-xs">
                        <button type="button" data-testid="rac-soggetti-all"
                          className="text-brand hover:underline"
                          onClick={() => setRacSoggettiSel(new Set(racSoggetti.filter((s) => s.email).map((s) => s.id)))}>
                          Seleziona tutti (con email)
                        </button>
                        <span className="text-slate-300">|</span>
                        <button type="button" data-testid="rac-soggetti-none"
                          className="text-slate-500 hover:underline"
                          onClick={() => setRacSoggettiSel(new Set())}>
                          Nessuno
                        </button>
                      </div>
                    )}
                  </div>
                  {racSoggetti.length > 0 && (
                    <>
                      <Input data-testid="rac-soggetti-search" type="search"
                        value={racSoggettiSearch}
                        onChange={(e) => setRacSoggettiSearch(e.target.value)}
                        placeholder="Cerca per nome, email o unità…"
                        className="h-10 mb-2" />
                      <div className="max-h-64 overflow-y-auto border-2 border-slate-200 rounded-lg divide-y divide-slate-100 bg-white">
                        {(() => {
                          const q = racSoggettiSearch.trim().toLowerCase();
                          const filtered = racSoggetti.filter((s) => {
                            if (!q) return true;
                            const un = (s.unita_collegate || []).map((u) => `${u.interno || ""} ${u.sub || ""}`).join(" ");
                            return `${s.nome || ""} ${s.email || ""} ${un}`.toLowerCase().includes(q);
                          });
                          if (filtered.length === 0) return <div className="p-3 text-sm text-slate-400 text-center">Nessun soggetto trovato</div>;
                          return filtered.map((s) => {
                            const checked = racSoggettiSel.has(s.id);
                            const hasEmail = !!s.email;
                            const unitaLabels = (s.unita_collegate || []).slice(0, 3).map((u) => {
                              const parts = [u.interno && `Int. ${u.interno}`, u.sub && `Sub ${u.sub}`, u.ruolo].filter(Boolean);
                              return parts.join(" · ");
                            }).filter(Boolean).join(" | ");
                            return (
                              <label key={s.id}
                                data-testid={`rac-soggetto-row-${s.id}`}
                                className={`flex items-start gap-3 p-2.5 cursor-pointer hover:bg-slate-50 ${!hasEmail ? "opacity-60" : ""}`}>
                                <input type="checkbox" data-testid={`rac-soggetto-check-${s.id}`}
                                  checked={checked}
                                  disabled={!hasEmail}
                                  onChange={(e) => {
                                    const next = new Set(racSoggettiSel);
                                    if (e.target.checked) next.add(s.id); else next.delete(s.id);
                                    setRacSoggettiSel(next);
                                  }}
                                  className="mt-1 w-5 h-5 accent-[#1C7C54]" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-slate-800 truncate">{s.nome || "—"}</div>
                                  <div className="text-xs text-slate-500 truncate">
                                    {hasEmail ? s.email : <span className="text-red-600 font-semibold">senza email — non selezionabile</span>}
                                    {unitaLabels ? ` · ${unitaLabels}` : ""}
                                  </div>
                                </div>
                              </label>
                            );
                          });
                        })()}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        Se non selezioni nessuno la campagna parte come prima, a tutti i soggetti con email. I soggetti senza email non sono selezionabili.
                      </p>
                    </>
                  )}
                  {!racSoggettiBusy && racSoggetti.length === 0 && (
                    <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                      Nessun soggetto trovato per questo condominio. Verifica in Anagrafica &gt; Soggetti.
                    </div>
                  )}
                </div>
              )}
              <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-2 text-amber-800">
                Nota: l'invio delle richieste anagrafiche deve essere attivo (Impostazioni → "Invio richieste anagrafiche"). Se è disattivo, riceverai un avviso e nessuna email verrà inviata.
              </div>
              <Button data-testid="rac-avvia-campagna" disabled={busy} onClick={avviaRac} className="h-11 bg-[#1C7C54] hover:bg-[#155c3e] font-bold">
                <Rocket className="w-4 h-4 mr-2" /> Avvia campagna RAC
              </Button>
            </div>
          ) : SCHEDA_META[sel] ? (
            <div className="space-y-3" data-testid="scheda-panel">
              <div className="border-2 border-brand/20 rounded-lg p-4 bg-brand/5">
                <div className="flex items-center gap-2 mb-1 text-brand font-bold"><FileText className="w-5 h-5" /> Scheda informativa & email di attivazione — {SCHEDA_META[sel].label}</div>
                <p className="text-sm text-slate-600">Personalizza il testo della <b>scheda informativa PDF</b> allegata all'email di attivazione (scaricabile anche in "Guida e manuali"). Formato: <code>## Titolo sezione</code> per i titoli, <code>- voce</code> per i punti elenco, riga vuota per separare.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button data-testid={`anteprima-email-${SCHEDA_META[sel].ruolo}`} variant="outline" size="sm" className="h-9" onClick={() => apriAnteprimaEmail(SCHEDA_META[sel].ruolo)} title="Vedi l'email di attivazione">
                  <Eye className="w-4 h-4 mr-1" /> Anteprima email attivazione
                </Button>
                <Button data-testid={`scarica-scheda-${SCHEDA_META[sel].ruolo}`} variant="outline" size="sm" className="h-9" onClick={() => scaricaScheda(SCHEDA_META[sel].ruolo)} title="Scarica anteprima PDF">
                  <FileText className="w-4 h-4 mr-1" /> Scarica PDF
                </Button>
              </div>
              <FormatToolbar
                textareaRef={schedaTextareaRef}
                value={schede[SCHEDA_META[sel].campo]}
                onChange={(v) => setSchede((s) => ({ ...s, [SCHEDA_META[sel].campo]: v }))}
                testid={`scheda-toolbar-${SCHEDA_META[sel].ruolo}`}
                titolareData={titolareData}
              />
              <Textarea ref={schedaTextareaRef} data-testid={`scheda-testo-${SCHEDA_META[sel].ruolo}`} value={schede[SCHEDA_META[sel].campo]}
                onChange={(e) => setSchede((s) => ({ ...s, [SCHEDA_META[sel].campo]: e.target.value }))}
                rows={16} className="font-mono text-sm leading-relaxed" placeholder="## Titolo sezione&#10;- voce" />
              <Button data-testid={`salva-scheda-${SCHEDA_META[sel].ruolo}`} disabled={schedeBusy} onClick={() => salvaScheda(SCHEDA_META[sel].campo)}
                className="h-11 bg-brand hover:bg-brand-dark font-bold">
                <Save className="w-4 h-4 mr-2" /> Salva scheda {SCHEDA_META[sel].ruolo}
              </Button>
            </div>
          ) : (
          <>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1">Titolo del modello *</label>
              <Input data-testid="modello-titolo" value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} className="h-12" placeholder="Es. Richiesta dati anagrafici" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Destinatari</label>
                <select data-testid="modello-destinatario" value={form.destinatario} onChange={(e) => setForm({ ...form, destinatario: e.target.value })}
                  className="h-12 w-full px-3 border-2 border-slate-300 rounded-lg bg-white text-base">
                  {DEST.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Tipo</label>
                <select data-testid="modello-categoria" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                  className="h-12 w-full px-3 border-2 border-slate-300 rounded-lg bg-white text-base">
                  {CATEGORIE.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1">Oggetto</label>
            <Input data-testid="modello-oggetto" value={form.oggetto} onChange={(e) => setForm({ ...form, oggetto: e.target.value })} className="h-12" placeholder="Oggetto della comunicazione" />
          </div>
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1">Testo</label>
            <FormatToolbar
              textareaRef={modelloTextareaRef}
              value={form.testo}
              onChange={(v) => setForm({ ...form, testo: v })}
              testid="modello-testo-toolbar"
              titolareData={titolareData}
            />
            <Textarea ref={modelloTextareaRef} data-testid="modello-testo" value={form.testo} onChange={(e) => setForm({ ...form, testo: e.target.value })} rows={9} className="text-sm leading-relaxed" placeholder="Scrivi il contenuto della comunicazione…" />
          </div>

          {(() => {
            const cur = sel ? list.find((x) => x.id === sel) : null;
            if (!cur?.allegato_predefinito) return null;
            return (
              <div className="border-2 border-brand/30 rounded-lg p-3 bg-brand/5 flex items-center gap-3" data-testid="modello-allegato-predefinito">
                <FileText className="w-5 h-5 text-brand shrink-0" />
                <div className="text-sm text-slate-700 min-w-0">
                  <div className="font-bold text-brand truncate">Allegato predefinito: {cur.allegato_predefinito.nome}</div>
                  <div className="text-xs text-slate-500">Verrà allegato automaticamente all'avviso quando premi "Usa per avviso ai condòmini".</div>
                </div>
              </div>
            );
          })()}

          {form.categoria === "Richiesta dati" && (
            <div className="border-2 border-dashed border-brand/30 rounded-lg p-3 bg-brand/5" data-testid="campi-editor">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-brand">Dati da raccogliere (campi del modulo)</span>
                <Button type="button" data-testid="campo-add" variant="outline" size="sm" className="h-8" onClick={addCampo}><Plus className="w-4 h-4 mr-1" /> Aggiungi campo</Button>
              </div>
              {(form.campi || []).length === 0 && <p className="text-xs text-slate-500">Nessun campo. Aggiungi le informazioni che vuoi richiedere (es. Cellulare, N. occupanti…).</p>}
              <div className="space-y-2">
                {(form.campi || []).map((c, i) => (
                  <div key={i} className="bg-white border rounded-lg p-2 flex flex-wrap items-center gap-2" data-testid={`campo-row-${i}`}>
                    <Input value={c.etichetta} onChange={(e) => setCampo(i, { etichetta: e.target.value })} placeholder="Etichetta (es. Cellulare)" className="h-10 flex-1 min-w-[150px]" data-testid={`campo-etichetta-${i}`} />
                    <select value={c.tipo} onChange={(e) => setCampo(i, { tipo: e.target.value })} className="h-10 px-2 border-2 border-slate-300 rounded-lg bg-white text-sm" data-testid={`campo-tipo-${i}`}>
                      {TIPI_CAMPO.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    {c.tipo === "scelta" && (
                      <Input value={(c.opzioni || []).join(", ")} onChange={(e) => setCampo(i, { opzioni: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                        placeholder="Opzioni separate da virgola" className="h-10 flex-1 min-w-[150px]" data-testid={`campo-opzioni-${i}`} />
                    )}
                    <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={!!c.obbligatorio} onChange={(e) => setCampo(i, { obbligatorio: e.target.checked })} data-testid={`campo-obbl-${i}`} /> obbligatorio</label>
                    <Button type="button" variant="ghost" size="sm" className="h-9 text-red-600" onClick={() => delCampo(i)} data-testid={`campo-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button data-testid="modello-salva" disabled={busy} onClick={salva} className="h-11 bg-brand hover:bg-brand-dark font-bold">
              <Save className="w-4 h-4 mr-2" /> {sel ? "Salva modifiche" : "Crea modello"}
            </Button>
            <Button data-testid="modello-copia" variant="outline" className="h-11" onClick={copia}>
              <Copy className="w-4 h-4 mr-2" /> Copia testo
            </Button>
            {form.categoria === "Richiesta dati" && (
              <Button data-testid="modello-avvia-raccolta" className="h-11 bg-[#1C7C54] hover:bg-[#155c3e] font-bold" onClick={() => { if (!form.titolo.trim() || !(form.campi || []).length) { toast.error("Inserisci titolo e almeno un campo, poi salva il modello"); return; } setLaunch(true); }}>
                <Rocket className="w-4 h-4 mr-2" /> Avvia raccolta dati
              </Button>
            )}
            {(form.destinatario === "condomini" || form.destinatario === "entrambi") && (
              <Button data-testid="modello-invia-condomini" variant="outline" className="h-11 border-brand text-brand" onClick={() => inviaCome("condomini")}>
                <Send className="w-4 h-4 mr-2" /> Usa per avviso ai condòmini
              </Button>
            )}
            {(form.destinatario === "fornitori" || form.destinatario === "entrambi") && (
              <Button data-testid="modello-invia-fornitori" variant="outline" className="h-11 border-brand text-brand" onClick={() => inviaCome("fornitori")}>
                <Send className="w-4 h-4 mr-2" /> Usa per comunicazione ai fornitori
              </Button>
            )}
          </div>
          </>
          )}
        </div>
      </div>

      <Dialog open={launch} onOpenChange={setLaunch}>
        <DialogContent data-testid="raccolta-launch-dialog">
          <DialogHeader><DialogTitle>Avvia raccolta dati</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2 mb-1">Ogni destinatario riceverà un'email con un link personale per compilare: <b>{form.titolo}</b>.</p>
          <div className="space-y-3">
            <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={lc.invia_condomini} onChange={(e) => setLc({ ...lc, invia_condomini: e.target.checked })} data-testid="lc-condomini" /> Invia ai condòmini</label>
            {lc.invia_condomini && (
              <select value={lc.condominio_id} onChange={(e) => setLc({ ...lc, condominio_id: e.target.value })} className="h-11 w-full px-3 border-2 border-slate-300 rounded-lg bg-white" data-testid="lc-condominio">
                <option value="">Tutti i condomìni</option>
                {condomini.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            )}
            <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={lc.invia_fornitori} onChange={(e) => setLc({ ...lc, invia_fornitori: e.target.checked })} data-testid="lc-fornitori" /> Invia ai fornitori</label>
            {lc.invia_fornitori && (
              <Input value={lc.categoria_fornitore} onChange={(e) => setLc({ ...lc, categoria_fornitore: e.target.value })} placeholder="Filtra per categoria (facoltativo, es. Idraulica)" className="h-11" data-testid="lc-categoria" />
            )}
            <p className="text-xs text-slate-400">Solo i destinatari con un indirizzo email riceveranno il link.</p>
            {/* Iter123k260 · Mappatura campo → scheda condomino (auto-apply) */}
            {lc.invia_condomini && form.campi && form.campi.length > 0 && Object.keys(targetFieldsAnag).length > 0 && (
              <div data-testid="auto-apply-mapping" className="border-2 border-indigo-200 rounded-lg p-3 bg-indigo-50/60">
                <div className="font-bold text-indigo-900 text-sm mb-1">
                  Aggiorna in automatico la scheda unità del condomino
                </div>
                <div className="text-[11px] text-indigo-800 mb-2">
                  Associa ogni campo del modulo al campo della scheda da aggiornare al momento dell'invio della risposta. Lascia "— nessuno —" per non aggiornare nulla.
                </div>
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {form.campi.map((c) => (
                    <div key={c.id} className="grid grid-cols-5 gap-2 items-center">
                      <div className="col-span-2 text-xs text-slate-700 truncate" title={c.etichetta}>
                        {c.etichetta || c.id}
                      </div>
                      <div className="col-span-3">
                        <select
                          data-testid={`map-${c.id}`}
                          value={autoApplyMapping[c.id] || ""}
                          onChange={(e) => setAutoApplyMapping((m) => ({ ...m, [c.id]: e.target.value }))}
                          className="h-9 w-full px-2 border-2 border-slate-200 rounded-md bg-white text-xs"
                        >
                          <option value="">— nessuno —</option>
                          {Object.entries(targetFieldsAnag).map(([k, lbl]) => (
                            <option key={k} value={k}>{lbl}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLaunch(false)} className="h-11">Annulla</Button>
            <Button data-testid="lc-conferma" disabled={busy} onClick={avvia} className="h-11 bg-[#1C7C54] hover:bg-[#155c3e] font-bold">
              <Rocket className="w-4 h-4 mr-2" /> Invia ora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!anteprima} onOpenChange={(o) => !o && setAnteprima(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="text-xl flex items-center gap-2"><Mail className="w-5 h-5 text-brand" /> Anteprima email — {anteprima?.ruolo}</DialogTitle></DialogHeader>
          {anteprima && (
            <div className="space-y-3" data-testid="anteprima-email-dialog">
              <div className="text-sm"><span className="text-slate-500">Oggetto:</span> <b>{anteprima.subject}</b></div>
              <div className="bg-slate-50 border rounded-lg p-4 text-sm whitespace-pre-wrap max-h-[55vh] overflow-y-auto" data-testid="anteprima-email-body">{anteprima.body}</div>
              <p className="text-xs text-slate-400">I valori ID di accesso e codice di attivazione qui sono di esempio: nelle email reali vengono inseriti automaticamente quelli di ciascun destinatario. Alla mail viene allegata la scheda informativa PDF.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
